/**
 * Persistence for the user's chosen display locale.
 *
 * `localStorage` keeps localization a single-package change — no backend contract, no deploy
 * ordering — at the cost of the choice not following a user to another device
 * (`.agents/planning/2026-10-12-panther-localization/design/design.md` §3 D4). This module is the
 * intended seam for the §5.3 migration to the server-side `displaySettings.locale`:
 * {@link getStoredLocale} must stay synchronous, so moving to the server also means hydrating
 * asynchronously and applying the result via `setLocalizationContext`.
 */
import { isSupportedLocale } from 'src/i18n/locales';

/** Matches the sibling display setting `langhub-dark-mode`; the two are intended to migrate together. */
export const LOCALE_STORAGE_KEY = 'langhub-locale';

/**
 * The persisted locale, or `null` when there is none to use. Validating here rather than at the
 * call site means a stale value — removed from the set, or written by a newer client — can never
 * reach the bundle manager, where it would resolve to a pack that was never published. Storage
 * access is guarded because it throws rather than returning null in private browsing and some
 * embedded webviews, matching `DarkModeContext`.
 */
export const getStoredLocale = (): string | null => {
  try {
    const stored = localStorage.getItem(LOCALE_STORAGE_KEY);
    return stored !== null && isSupportedLocale(stored) ? stored : null;
  } catch {
    return null;
  }
};

/** Silent on failure by design: losing the persisted preference degrades the next load to the
 * default locale, which is better than failing the interaction the user just performed. */
export const setStoredLocale = (locale: string): void => {
  if (!isSupportedLocale(locale)) {
    return;
  }
  try {
    localStorage.setItem(LOCALE_STORAGE_KEY, locale);
  } catch {
    return;
  }
};
